import React from 'react';
import { Box, Button, Dialog, IconButton, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { MoreHoriz, Refresh } from '@mui/icons-material';
import ProcoreCopyConfig from '@components/common/ProcoreCopyConfig';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import API from '@API/index';
import { v4 } from 'uuid';
import { useSnackbar } from 'notistack';
import Utils from '@utils/index';

type VideoOptionsMenuProps = {
  onReload?: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onActivate?: () => void;
  details?: boolean;
}

export const VideoOptionsMenu: React.FC<VideoOptionsMenuProps> = (props) => {
  const { onReload, onEdit, onArchive, onDelete, onActivate, details } = props;

  const { enqueueSnackbar } = useSnackbar();

  const selectedStream = useSelector((store: IStoreState) => store.selectedStream.stream);
  const isSuperAdministrator = useSelector((store: IStoreState) => store.session.isSuperAdministrator);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isCreatingYoutube, setIsCreatingYoutube] = React.useState<boolean>(false);
  const [isYoutubeConfigured, setIsYoutubeConfigured] = React.useState<boolean>(false);
  const [youtubeKey, setYoutubeKey] = React.useState<string>(selectedStream?.youtubeStreamKey || "");

  React.useEffect(() => {
    if (isCreatingYoutube === false) setYoutubeKey(selectedStream?.youtubeStreamKey || "");
  }, [isCreatingYoutube, selectedStream?.youtubeStreamKey])

  React.useEffect(() => {
    if (selectedStream)
      API.Streams.isYoutubeConfigured(selectedStream)
        .then((response) => {
          setIsYoutubeConfigured(response)
        })
        .catch((error) => {
          console.log(error);
        })
  }, [selectedStream])

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, [])

  const handleReload = React.useCallback(() => {
    if (onReload) onReload();
    handleClose();
  }, [handleClose, onReload])

  const handleCreateYoutubeStream = React.useCallback(() => {
    if (selectedStream) {
      let EDGE_DEVICE = process.env.REACT_APP_STACK === 'prod' ? 'a8_a1_59_da_31_b1' : 'a8_a1_59_9e_d4_54';

      if (selectedStream.iotThingName && process.env.REACT_APP_STACK === 'prod') {
        if (selectedStream.iotThingName !== "DETACHED") {
          EDGE_DEVICE = selectedStream.iotThingName;
        }
      }

      const uuid: string = v4();

      const sub = API.PubSub.subscribeToTopic(`edge/${EDGE_DEVICE}/http/responses/${uuid}`, (message: any) => {
        console.log("Incoming message: ", message);
        sub?.unsubscribe();

        if (message?.status === 500) {
          enqueueSnackbar(`Request failed with error: ${message?.message || "Internal server error."}`, { variant: 'error' });
        } else {
          enqueueSnackbar('Stream created!', { variant: 'success' });
          setIsYoutubeConfigured(true);
        }
      });

      const message = {
        "device": {
          "manufacturer": "Edge",
          "url": Utils.RegEx.findIPAddress(selectedStream?.rtspUrl || "")
        },
        "action": "streaming/youtube/create",
        "parameters": {
          "kvsName": selectedStream.kvsName,
          "youtubeStreamKey": youtubeKey
        }
      };

      API.PubSub.publishOnTopic(`edge/${EDGE_DEVICE}/http/requests/${uuid}`, message);
    }
  }, [enqueueSnackbar, selectedStream, youtubeKey])


  const handleDeleteYoutubeStream = React.useCallback(() => {
    if (selectedStream) {
      let EDGE_DEVICE = process.env.REACT_APP_STACK === 'prod' ? 'a8_a1_59_da_31_b1' : 'a8_a1_59_9e_d4_54';

      if (selectedStream.iotThingName && process.env.REACT_APP_STACK === 'prod') {
        if (selectedStream.iotThingName !== "DETACHED") {
          EDGE_DEVICE = selectedStream.iotThingName;
        }
      }

      const uuid: string = v4();

      const sub = API.PubSub.subscribeToTopic(`edge/${EDGE_DEVICE}/http/responses/${uuid}`, (message: any) => {
        console.log("Incoming message: ", message);
        sub?.unsubscribe();

        if (message?.status === 500) {
          enqueueSnackbar(`Request failed with error: ${message?.message || "Internal server error."}`, { variant: 'error' });
        } else {
          enqueueSnackbar('Stream deleted!', { variant: 'success' });
          setIsYoutubeConfigured(false);
        }
      });

      const message = {
        "device": {
          "manufacturer": "Edge",
        },
        "action": "streaming/youtube/delete",
        "parameters": {
          "kvsName": selectedStream.kvsName
        }
      };

      API.PubSub.publishOnTopic(`edge/${EDGE_DEVICE}/http/requests/${uuid}`, message);
    }
  }, [enqueueSnackbar, selectedStream])

  if (onEdit === undefined && onArchive === undefined && onDelete === undefined && onActivate === undefined) {
    return (
      <Tooltip title="Reload" arrow placement="right">
        <IconButton onClick={onReload} size="small">
          <Refresh color="action" />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <>
      <Tooltip title="More options" arrow placement="right">
        <IconButton data-cy="video-player-more-options" onClick={handleClick} size="small">
          <MoreHoriz color="action" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleReload} data-cy={`video-reload-menu-item`} >Reload</MenuItem>
        {onEdit && <MenuItem onClick={onEdit} data-cy={`video-edit-menu-item`} >{details ? "Details" : "Edit"}</MenuItem>}
        {onActivate && <MenuItem onClick={() => { handleClose(); onActivate(); }} data-cy={`video-activate-menu-item`} >Activate</MenuItem>}
        {onArchive && <MenuItem onClick={() => { handleClose(); onArchive(); }} data-cy={`video-archive-menu-item`} >Archive</MenuItem>}
        {onDelete && <MenuItem onClick={() => { handleClose(); onDelete(); }} data-cy={`video-delete-menu-item`} >Delete</MenuItem>}
        {isSuperAdministrator && !isYoutubeConfigured && <MenuItem onClick={() => { handleClose(); setIsCreatingYoutube(true); }} data-cy={`video-delete-menu-item`} >Create youtube stream</MenuItem>}
        {isSuperAdministrator && isYoutubeConfigured && <MenuItem onClick={() => { handleClose(); handleDeleteYoutubeStream(); }} data-cy={`video-delete-menu-item`} >Delete youtube stream</MenuItem>}
        <ProcoreCopyConfig onClick={() => { handleClose(); }} />
      </Menu>
      <Dialog
        open={isCreatingYoutube}
        onClose={() => setIsCreatingYoutube(false)}
      >
        <Box padding="1.5rem" width="25vw">
          <Box paddingBottom="1rem">
            <TextField
              fullWidth
              id="youtube-key-input"
              label="Youtube stream key"
              value={youtubeKey}
              placeholder="Enter YouTube stream key"
              onChange={(e) => setYoutubeKey(e.target.value)}
              size="small"
              variant="outlined"
              inputProps={{ maxLength: 50 }}
              data-cy="video-options-youtube-key"
            />
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" width="100%">
            <Button
              color="secondary"
              onClick={() => setIsCreatingYoutube(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={youtubeKey.length < 1}
              onClick={() => {
                setIsCreatingYoutube(false);
                handleCreateYoutubeStream();
              }}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}